import { useCallback, useContext, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";
import { Joyride } from "react-joyride";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import Button, {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS
} from "components/DesignComponents/Button";
import TutorialContext from "context/tutorial/tutorialContext";
import { FALSE, TUTORIAL_INITIAL_RENDER } from "shared/constants/strings";
import { useMediaQueries } from "shared/hooks/useMediaQueries";

import {
    COMPANY_SELECTOR_BUTTON,
    COMPANY_SELECTOR_BUTTON_MOBILE,
    CONTROLS_ITEM_FRAME_CLOSE_BUTTON,
    DIALOG_CUSTOM_CLOSE_BUTTON
} from "./constants";
import { TutorialTooltip } from "./TutorialTooltip";
import { STEP_GROUP } from "./types/enums";
import useTutorialStepGroups from "./useTutorialStepGroups";

const TutorialMode = () => {
    const { white } = colors;

    const {
        isTutorialOpen,
        setTutorialOpen,
        stepGroup,
        stepIndex,
        setDrawerStatus,
        setInitialRender
    } = useContext(TutorialContext);

    const { t } = useTranslation();

    const {
        companySelectorSteps,
        finishTutorialSteps,
        firstTimeUserSteps,
        taskGroupsSteps,
        taskGroupsStepsMobile
    } = useTutorialStepGroups();

    const { fromMd, toSm } = useMediaQueries();

    const handleExit = useCallback(() => {
        setTutorialOpen(false, 0, STEP_GROUP.TutorialCompanySelector);
        document.getElementById(CONTROLS_ITEM_FRAME_CLOSE_BUTTON)?.click();
        setDrawerStatus(false);

        localStorage.setItem(TUTORIAL_INITIAL_RENDER, FALSE);
        setInitialRender(false);
    }, [setDrawerStatus, setInitialRender, setTutorialOpen]);

    const firstRender = useRef(true);

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }

        handleExit();
        //eslint-disable-next-line
    }, [fromMd, toSm]);

    useEffect(() => {
        const closeTutorial = (e: any) => {
            if (e.key === "Escape") {
                handleExit();
            }
        };

        window.addEventListener("keydown", closeTutorial);

        return () => {
            window.removeEventListener("keydown", closeTutorial);
        };
    }, [handleExit]);

    const handleEvent = () => {
        if (
            stepGroup === STEP_GROUP.TutorialCompanySelector &&
            stepIndex === 1
        ) {
            const id = toSm
                ? COMPANY_SELECTOR_BUTTON_MOBILE
                : COMPANY_SELECTOR_BUTTON;

            // opens company selector
            document.getElementById(id)?.click();
        } else {
            // closes company selector on desktop
            document.getElementById(CONTROLS_ITEM_FRAME_CLOSE_BUTTON)?.click();
            // closes company selector on mobile
            document.getElementById(DIALOG_CUSTOM_CLOSE_BUTTON)?.click();
        }
    };

    const currentStepGroup = () => {
        switch (stepGroup) {
            case STEP_GROUP.FirstTimeUser:
                return firstTimeUserSteps;
            case STEP_GROUP.FinishTutorial:
                return finishTutorialSteps;
            case STEP_GROUP.TutorialTaskGroups:
                return fromMd ? taskGroupsSteps : taskGroupsStepsMobile;
            default:
                return companySelectorSteps;
        }
    };

    return (
        <>
            <Joyride
                floatingOptions={{ hideArrow: true }}
                steps={currentStepGroup()}
                stepIndex={stepIndex}
                onEvent={handleEvent}
                run={isTutorialOpen}
                tooltipComponent={TutorialTooltip}
                options={{
                    skipBeacon: true,
                    scrollOffset: 100,
                    zIndex: 10000,
                    overlayClickAction: false
                }}
            />

            {isTutorialOpen && (
                <div
                    css={css({
                        width: "100%",
                        display: "flex",
                        justifyContent: "center"
                    })}
                >
                    <div
                        css={css({
                            display: "flex",
                            justifyContent: "space-around",
                            alignItems: "center",
                            fontWeight: 600,
                            padding: "8px",
                            zIndex: 1000000,
                            maxWidth: "233px",
                            width: "100%",
                            bottom: "32px",
                            position: "fixed",
                            backgroundColor: white,
                            borderRadius: "12px",
                            boxShadow: "0px 4px 16px rgba(15, 23, 42, 0.16)"
                        })}
                    >
                        <div>{t("Tutorial##titles##tutorial mode")}</div>

                        <Button
                            color={BUTTON_COLORS.Primary}
                            size={BUTTON_SIZES.Small}
                            variant={BUTTON_VARIANTS.TextOnly}
                            onClick={handleExit}
                        >
                            {t("Tutorial##buttons##exit")}
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default TutorialMode;
